import React, { useCallback } from 'react';
import useTranslations from '../../../hooks/useTranslations';
import { CONSTANTS } from '../../../utils/constants';
import { IImportedAssignmentItem } from '../../../utils/customerAssignmentsExcel';
import { Add, Delete } from '@mui/icons-material';
import './EditableAssignmentsTable.scss';

interface EditableAssignmentsTableProps {
  assignments: IImportedAssignmentItem[];
  onChange: (items: IImportedAssignmentItem[]) => void;
}

const phoneRegex =
  CONSTANTS.REGEX_PATTERN_VALIDATORS
    .PHONE_NUMBER_WITH_OR_WITHOUT_ISRAEL_COUNTRY_CODE;

const EditableAssignmentsTable: React.FC<EditableAssignmentsTableProps> = ({
  assignments,
  onChange,
}) => {
  const { rcTranslate } = useTranslations();

  const defaultRow = {
    fullNameFromContractor: '',
    phoneNumber: '',
    lockerNumber: '',
    sBSubscriptionVoucher: false,
    upgradeToSmartLockerVoucher: false,
  };

  const updateField = useCallback(
    (index: number, field: keyof IImportedAssignmentItem, value: any) => {
      const currentAssignments = [...assignments];
      currentAssignments[index] = {
        ...currentAssignments[index],
        [field]: value,
      };
      onChange(currentAssignments);
    },
    [assignments, onChange]
  );

  const addRow = useCallback(() => {
    onChange([...assignments, defaultRow]);
  }, [assignments, onChange]);

  const removeRow = useCallback(
    (index: number) => {
      onChange(assignments.filter((_, i) => i !== index));
    },
    [assignments, onChange]
  );

  const phoneNumbersOccurrences = assignments
    .map((a) => a.phoneNumber.trim())
    .reduce<Record<string, number>>((acc, cur) => {
      acc[cur] = (acc[cur] || 0) + 1;
      return acc;
    }, {});

  const lockerNumbersOccurrences = assignments
    .map((a) => a.lockerNumber?.trim())
    .filter((p) => p !== undefined)
    .reduce<Record<string, number>>((acc, cur) => {
      acc[cur!] = (acc[cur!] || 0) + 1;
      return acc;
    }, {});

  const EditableAssignmentsTableCheckboxCell = ({
    idx,
    checked,
    field,
  }: {
    idx: number;
    checked: boolean;
    field: keyof IImportedAssignmentItem;
  }) => {
    return (
      <td className="editableAssignmentsTable__checkboxCell">
        <input
          type="checkbox"
          checked={checked}
          onChange={(e) => updateField(idx, field, e.target.checked)}
        />
      </td>
    );
  };

  const EmptyAssignmentsRow = () =>
    assignments.length === 0 ? (
      <tr>
        <td colSpan={5} className="editableAssignmentsTable__emptyCell">
          {rcTranslate('customerAssignments.insertRange.importedCount')}: 0
        </td>
      </tr>
    ) : null;

  return (
    <div className="editableAssignmentsTable">
      <div className="editableAssignmentsTable__header">
        <div className="editableAssignmentsTable__count">
          <strong>
            {rcTranslate('customerAssignments.insertRange.importedCount')}:
          </strong>
          <span>{assignments.length}</span>
        </div>
        <Add onClick={addRow} className="editableAssignmentsTable__addIcon" />
      </div>
      <div className={`editableAssignmentsTable__scrollWrapper`}>
        <table>
          <thead>
            <tr>
              {[
                'fullNameFromContractor',
                'phoneNumber',
                'lockerNumber',
                'sBSubscriptionVoucher',
                'upgradeToSmartLockerVoucher',
              ].map((label) => (
                <th key={label}>
                  {rcTranslate(
                    `customerAssignments.insertRange.table.${label}`
                  )}
                </th>
              ))}
              <th></th>
            </tr>
          </thead>
          <tbody>
            {assignments.map((a, i) => {
              const phone = a.phoneNumber.trim();
              const invalidPhone = phone.length > 0 && !phoneRegex.test(phone);
              const isDuplicatePhoneNumber = phoneNumbersOccurrences[phone] > 1;

              const locker = a.lockerNumber?.trim();
              const isDuplicateLockerNumber = locker
                ? lockerNumbersOccurrences[locker] > 1
                : false;
              return (
                <tr key={i}>
                  <td>
                    <input
                      value={a.fullNameFromContractor ?? ""}
                      onChange={(e) =>
                        updateField(i, 'fullNameFromContractor', e.target.value)
                      }
                      maxLength={250}
                      placeholder={rcTranslate(
                        'customerAssignments.insertRange.table.fullNameFromContractor'
                      )}
                    />
                  </td>
                  <td>
                    <input
                      value={a.phoneNumber}
                      onChange={(e) =>
                        updateField(i, 'phoneNumber', e.target.value)
                      }
                      className={`phoneInput ${invalidPhone || isDuplicatePhoneNumber ? 'invalid' : ''}`}
                      maxLength={10}
                      placeholder={rcTranslate(
                        'customerAssignments.insertRange.table.phoneNumber'
                      )}
                    />
                    {(invalidPhone || isDuplicatePhoneNumber) && (
                      <div className="errorText">
                        {invalidPhone &&
                          rcTranslate(
                            'customerAssignments.insertRange.table.invalidPhone'
                          )}
                        {isDuplicatePhoneNumber &&
                          rcTranslate(
                            'customerAssignments.insertRange.table.duplicatePhone'
                          )}
                      </div>
                    )}
                  </td>
                  <td>
                    <input
                      value={a.lockerNumber || ''}
                      onChange={(e) =>
                        updateField(i, 'lockerNumber', e.target.value)
                      }
                      className={`lockerInput ${isDuplicateLockerNumber ? 'invalid' : ''}`}
                      placeholder={rcTranslate(
                        'customerAssignments.insertRange.table.lockerNumber'
                      )}
                    />
                    {isDuplicateLockerNumber && (
                      <div className="errorText">
                        {` (${rcTranslate('customerAssignments.insertRange.table.duplicateLocker')})`}
                      </div>
                    )}
                  </td>
                  <EditableAssignmentsTableCheckboxCell
                    idx={i}
                    checked={Boolean(a.sBSubscriptionVoucher)}
                    field="sBSubscriptionVoucher"
                  />
                  <EditableAssignmentsTableCheckboxCell
                    idx={i}
                    checked={Boolean(a.upgradeToSmartLockerVoucher)}
                    field="upgradeToSmartLockerVoucher"
                  />
                  <td>
                    <Delete
                      className="deleteIcon"
                      onClick={() => removeRow(i)}
                    />
                  </td>
                </tr>
              );
            })}
            <EmptyAssignmentsRow />
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default EditableAssignmentsTable;
